// Checkout Success Section - Displays success message after Stripe checkout
// Follows the Section, Component & Manager System architecture

/**
 * TODO: Checkout Flow After Real Stripe Integration
 * 
 * PLAN FOR REAL CHECKOUT SYSTEM:
 * 
 * 1. Direct Navigation (No Payload):
 *    - If user navigates to /checkout directly without any payload or session_id,
 *      automatically redirect them to the pricing page (doesn't make sense to be here)
 * 
 * 2. Stripe Redirect Back:
 *    - When Stripe redirects back after successful checkout, we'll receive a session_id
 *    - Need to investigate if the payload system can work with Stripe redirects
 *    - Options:
 *      a) Use session_id to fetch checkout session details from backend
 *      b) Use URL query params that Stripe can pass (if payload system doesn't work)
 *      c) Store checkout intent in sessionStorage before redirecting to Stripe
 * 
 * 3. Payload-Based Navigation:
 *    - When navigated via payload system (e.g., from pricing section),
 *      display the success page with plan details from payload
 * 
 * 4. Auto-Redirect Behavior:
 *    - CURRENTLY DISABLED: We're NOT auto-redirecting to pricing when no payload exists
 *      because we need to be able to test the checkout page directly during development
 *    - FUTURE: Once real checkout is working, enable auto-redirect for direct navigation
 *      (no payload, no session_id) → redirect to pricing page
 * 
 * IMPLEMENTATION NOTES:
 * - The payload system may not work with external redirects (Stripe → our site)
 * - May need to use URL query params or sessionStorage as fallback
 * - Backend webhook will handle actual subscription creation, this page is just for UX
 */

import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { CheckoutManager } from './managers/checkout-manager';
import { useManager } from '../../core';
import { useSectionPayload } from '../../managers/navigation-manager';
import { useAuth } from '../../contexts/AuthContext';
import { CheckoutSuccess } from './components';
import './CheckoutSection.css';

export function CheckoutSuccessSection() {
  const { manager, isInitialized } = useManager(CheckoutManager);
  const { payload } = useSectionPayload();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const fetchedSessionRef = useRef(new Set());

  // Get session_id from URL params (from Stripe redirect)
  const sessionId = searchParams.get('session_id');
  
  // For testing: get plan data from URL params (fallback)
  const testPlanId = searchParams.get('planId');
  const testTierId = searchParams.get('tierId');
  const testBillingCycle = searchParams.get('billingCycle');
  const testPrice = searchParams.get('price');

  useEffect(() => {
    if (!isInitialized) return;

    // Priority 1: Handle payload from pricing section
    if (payload && payload.action === 'checkout_success') {
      manager.handlePayload(payload);
      // Refresh user data after successful checkout to update header
      refreshUser();
    }
    // Priority 2: Handle Stripe session_id (from real checkout)
    else if (sessionId) {
      // Prevent refetch loop: only fetch once per sessionId
      if (fetchedSessionRef.current.has(sessionId)) return;
      fetchedSessionRef.current.add(sessionId);

      // Fetch checkout session details from backend
      // This will also trigger transaction update if session is completed
      manager.fetchSessionDetails(sessionId)
        .then(() => {
          // After successful checkout, refresh user data to update header (credits, membership, etc.)
          refreshUser();
        })
        .catch((error) => {
          console.error('Failed to fetch session details:', error);
          // Fallback: set basic session data
          manager.setSessionData({
            sessionId,
          });
        });
    }
    // Priority 3: Fallback to URL params (for direct testing)
    else if (testPlanId) {
      manager.setSessionData({
        planId: testPlanId,
        tierId: testTierId,
        billingCycle: testBillingCycle,
        price: testPrice,
      });
    }
    // TODO: Future - Auto-redirect if no payload, no session_id, and no test params
    // else {
    //   // Direct navigation without any context - redirect to pricing
    //   navigationManager.navigateTo('pricing');
    // }
  }, [isInitialized, payload, sessionId, testPlanId, testTierId, testBillingCycle, testPrice, manager, refreshUser]);

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--checkout-success">
        <div className="checkout-success-container">
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            Loading...
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--checkout-success">
      <div className="checkout-success-container">
        <CheckoutSuccess
          sessionData={manager.getSessionData()}
          onGoToAccount={manager.navigateToAccount.bind(manager)}
          onGoToPricing={manager.navigateToPricing.bind(manager)}
        />
      </div>
    </MainLayout>
  );
}
